import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { CalendarOff, Check, Info, RotateCcw, Save, Shield, Trash2, Undo2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { currencySymbol, formatRateSym } from '@/lib/calendar-utils'
import { computeRunningTotals, engineComputedTotal, linkedOffsetLabel } from '@/lib/pipeline-utils'
import { markRecomputeExpected } from '@/lib/expectedRecompute'
import { resolveDateSpan } from '@/lib/date-span'
import { apiFetch, apiErrorMessage } from '@/lib/csrf'
import AnimatedRate from './AnimatedRate'
import BulkOverrideReview from './BulkOverrideReview'
import ClearOverridesDialog from './ClearOverridesDialog'
import { nestRoomRows } from '@/lib/room-type-tree'
import type { PageIdentity } from '@/types/review'
import type {
  RoomType,
  RateData,
  Adjustment,
  OverridePreviewResponse,
  OverridePreviewDrafted,
  OverridePreviewChild,
  OverrideRangePreview,
  OverrideScope,
} from '@/types/calendar'

// Same glyph DayDetailOverview uses for linked-child rows — keeps the hierarchy
// language identical across the Overview and Edit tabs.
const LINK_GLYPH = 'M19 15l-6 6-1.42-1.42L15.17 16H4V4h2v10h9.17l-3.59-3.58L13 9l6 6z'

const RECOMPUTE_TIMEOUT_MS = 30_000
const PREVIEW_DEBOUNCE_MS = 400

// The all-rooms percentage shortcuts. `%` is the only relative flavour that
// ships (an absolute per-night delta was deliberately left out — two input
// columns is what makes this form readable).
const NEG_CHIPS = [-20, -15, -10, -5]
const POS_CHIPS = [5, 10, 15, 20]

// A one-night minimum is the ABSENCE of a minimum, and computed_rates defaults
// the column to 1 — so every computed rate carries it. Treating that as an
// operator-set restriction made a plain price override save as override_type
// 'both' with min_stay_arrival 1 attached. The engine already draws the line
// here (RestrictionsStage counts a min stay as active only when > 1); this is
// the form agreeing with it.
const NO_MIN_STAY = 1

function meaningfulMinStay(value: number | null | undefined): string {
  return value != null && value > NO_MIN_STAY ? String(value) : ''
}

/** A relative adjustment intent. Resolved to a concrete amount by the backend
 *  preview — the frontend NEVER computes the resulting rate. */
interface AdjustIntent {
  type: 'percentage'
  value: number
}

/** One draft sent to /overrides/preview: EITHER an absolute typed rate OR a
 *  relative adjust the server resolves. Matches the mutation's contract. */
type DraftEntry =
  | { room_type_id: number; rate_amount: string }
  | { room_type_id: number; adjust: AdjustIntent }
  | { room_type_id: number; remove: true }

interface DayDetailEditPricesProps {
  date: string
  roomTypes: RoomType[]
  rates: Record<string, RateData>
  currency: string
  /** The property's backend-served canonical path. Every request target is this + a suffix. */
  propertyBasePath: string
  isPastDate: boolean
  /** Today AT THE PROPERTY (YYYY-MM-DD) — the floor for every date input here.
   *  Passed down rather than read off the viewer's clock: an operator ahead of
   *  the hotel would otherwise be refused a night the hotel is still selling. */
  today: string
  onDirtyChange: (dirty: boolean) => void
  onSaved: () => void
  /** True when rooms display prices from DIFFERENT rate plans — the Current line
   *  then names each row's plan (uniform case is covered by the modal header chip). */
  mixedPlans?: boolean
  /** The page's `pageIdentity`, for the review's hotel and business (change safety §5.10.4). */
  pageIdentity: PageIdentity | null | undefined
}

/**
 * The batch endpoints' `{ notice }` (change safety R1.10): the server's sentence naming the hotel, or
 * null when the answer carries none (an older server, a body that is not JSON).
 */
async function readNotice(response: Response): Promise<string | null> {
  try {
    const body = (await response.json()) as { notice?: unknown } | null
    return typeof body?.notice === 'string' && body.notice.trim() !== '' ? body.notice : null
  } catch {
    return null
  }
}

interface OverrideRow {
  /** A typed absolute price. Mutually exclusive with `pct` — whichever column
   *  the operator touched last wins, and that choice IS the saved mode. */
  rateAmount: string
  /** A typed/chipped percentage. Empty string when the row is in fixed mode. */
  pct: string
  reason: string
  stopSell: boolean
  closedToArrival: boolean
  closedToDeparture: boolean
  minStayArrival: string
  maxStay: string
  clearOverride: boolean
}

/** The percentage a saved relative override carries, read back off the stored
 *  adjustment trace. Display only — the backend stamped it. */
function savedPct(rate: RateData | undefined): string {
  const adj = rate?.computationDetails?.adjustments?.find((a) => a.stage === 'override')
  if (!adj || adj.type !== 'percentage' || adj.modifier_pct == null) return ''
  return String(adj.modifier_pct)
}

/**
 * How a room's CURRENT price came to be — read off the stored row, never off the
 * draft. This is the one chip on the row, and it is what the operator was missing
 * when a +20% override read as "Override" with an empty field: the rule that made
 * £78 out of £65 has to be on screen before a +5% chip can make sense.
 */
type StoredRule =
  | { kind: 'engine' }
  | { kind: 'fixed'; amount: number }
  | { kind: 'relative'; pct: string }
  | { kind: 'follows' }
  | { kind: 'separate'; amount: number; pct: string }

function storedRuleOf(rt: RoomType, rate: RateData | undefined): StoredRule {
  const isChild = rt.parentRoomTypeId != null
  if (rate?.source === 'manual_override') {
    const pct = savedPct(rate)
    if (isChild) return { kind: 'separate', amount: rate.rateAmount, pct }
    return pct === '' ? { kind: 'fixed', amount: rate.rateAmount } : { kind: 'relative', pct }
  }
  return isChild ? { kind: 'follows' } : { kind: 'engine' }
}

function deriveOverrideType(row: OverrideRow): string {
  const hasRate = row.rateAmount !== '' || row.pct !== ''
  const hasRestriction =
    row.stopSell ||
    row.closedToArrival ||
    row.closedToDeparture ||
    row.minStayArrival !== '' ||
    row.maxStay !== ''
  if (hasRate && hasRestriction) return 'both'
  if (hasRestriction) return 'restriction'
  return 'rate'
}

function buildInitialRow(rate: RateData | undefined): OverrideRow {
  if (rate && rate.source === 'manual_override') {
    const overrideAdj = rate.computationDetails?.adjustments?.find((a) => a.stage === 'override')
    const raw = overrideAdj?.description ?? ''
    // "Manual override: reason" | "Manual override +10%: reason"
    const existingReason = raw.replace(/^Manual override[^:]*:\s*/, '')
    const pct = savedPct(rate)
    return {
      // A relative override has no per-day amount of its own — the percentage IS
      // the instruction, so the price column shows the resolved figure instead.
      rateAmount: pct === '' && rate.rateAmount != null ? String(rate.rateAmount) : '',
      pct,
      reason: existingReason === 'no reason' ? '' : existingReason,
      stopSell: rate.stopSell,
      closedToArrival: rate.closedToArrival,
      closedToDeparture: rate.closedToDeparture,
      minStayArrival: meaningfulMinStay(rate.minStayArrival),
      maxStay: rate.maxStay != null ? String(rate.maxStay) : '',
      clearOverride: false,
    }
  }
  return {
    rateAmount: '',
    pct: '',
    reason: '',
    stopSell: false,
    closedToArrival: false,
    closedToDeparture: false,
    minStayArrival: '',
    maxStay: '',
    clearOverride: false,
  }
}

function buildInitialRows(
  roomTypes: RoomType[],
  rates: Record<string, RateData>,
  date: string
): Record<number, OverrideRow> {
  const rows: Record<number, OverrideRow> = {}
  for (const rt of roomTypes) {
    rows[rt.id] = buildInitialRow(rates[`${rt.id}:${date}`])
  }
  return rows
}

/**
 * Short offset label for a linked child's chip ("−20%" / "+£15").
 * Pure formatting of the stored offset config — no rate math.
 */
function followOffsetShort(rt: RoomType, sym: string): string {
  return linkedOffsetLabel(
    { offset_type: rt.parentOffsetType ?? undefined, offset_value: rt.parentOffsetValue ?? 0 } as Adjustment,
    sym
  )
}

function signedPct(p: number | string): string {
  const n = typeof p === 'string' ? parseFloat(p) : p
  if (Number.isNaN(n)) return ''
  return `${n > 0 ? '+' : ''}${n}%`
}

/** A single percentage shortcut. Green for uplifts, rose for cuts. */
function ChipButton({
  pct,
  selected,
  onPick,
}: {
  pct: number
  selected: boolean
  onPick: (pct: number) => void
}) {
  const positive = pct > 0
  return (
    <button
      type="button"
      onClick={() => onPick(pct)}
      className={cn(
        'rounded-lg border px-2.5 py-1 text-xs font-semibold tabular-nums transition-all duration-150 active:scale-95',
        positive
          ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300'
          : 'border-rose-200 bg-rose-50 text-rose-600 hover:border-rose-300',
        selected && positive && 'border-emerald-600 bg-emerald-600 text-white hover:border-emerald-600',
        selected && !positive && 'border-rose-600 bg-rose-600 text-white hover:border-rose-600'
      )}
    >
      {`${positive ? '+' : ''}${pct}%`}
    </button>
  )
}

/**
 * The status line's one sentence: `now → after · rule`. Both figures are backend
 * numbers (the stored rate and a preview result); the only thing decided here is
 * the colour, by direction. Renders "…" for the after figure while a preview is
 * in flight so the line never shows a stale result as if it were the new one.
 */
function PriceMove({
  from,
  to,
  rule,
  tone,
  loading,
  sym,
}: {
  from: number | null
  to: number | null
  rule: string
  tone?: 'rose' | 'amber'
  loading?: boolean
  sym: string
}) {
  const pending = to == null
  const colour =
    tone === 'rose'
      ? 'text-rose-600'
      : tone === 'amber'
        ? 'text-amber-600'
        : from != null && to != null && to > from
          ? 'text-emerald-600'
          : from != null && to != null && to < from
            ? 'text-rose-600'
            : 'text-muted-foreground'
  return (
    <span
      className={cn(
        'truncate whitespace-nowrap text-[10px] font-medium tabular-nums transition-opacity',
        loading && 'opacity-60'
      )}
      data-testid="price-move"
    >
      {from != null && (
        <>
          <span className="text-muted-foreground">{formatRateSym(from, sym)}</span>
          <span className="mx-1 text-muted-foreground/60">→</span>
        </>
      )}
      <span className={colour}>{pending ? '…' : formatRateSym(to as number, sym)}</span>
      <span className="ml-1 text-muted-foreground">· {rule}</span>
    </span>
  )
}

/** How many restrictions a row currently carries — drives the drill-down badge. */
function restrictionCount(row: OverrideRow): number {
  return [
    row.stopSell,
    row.closedToArrival,
    row.closedToDeparture,
    row.minStayArrival !== '',
    row.maxStay !== '',
  ].filter(Boolean).length
}

/**
 * Restrictions live behind a drill-down rather than inline.
 *
 * Inline they consumed the width the three price columns need, and they are the
 * rarer edit — a price change happens every session, a stop-sell rarely. The
 * trigger carries a count so an active restriction is never hidden.
 */
function RestrictionsDrilldown({
  row,
  rtId,
  roomName,
  disabled,
  updateRow,
}: {
  row: OverrideRow
  rtId: number
  roomName: string
  disabled: boolean
  updateRow: (id: number, patch: Partial<OverrideRow>) => void
}) {
  const count = restrictionCount(row)
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          aria-label={`Restrictions for ${roomName}${count > 0 ? ` (${count} active)` : ''}`}
          className={cn(
            'h-9 gap-1.5 rounded-lg px-2.5 text-xs font-medium transition-colors',
            count > 0
              ? 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100'
              : 'text-muted-foreground'
          )}
        >
          <Shield className="h-3.5 w-3.5" />
          {count > 0 ? <span className="tabular-nums">{count}</span> : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-3">
        {/* No room name — the popover is anchored to that room's row. */}
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Restrictions
        </div>
        <RestrictionsControls row={row} rtId={rtId} disabled={disabled} updateRow={updateRow} />
      </PopoverContent>
    </Popover>
  )
}

/** Restrictions controls */
function RestrictionsControls({
  row,
  rtId,
  disabled,
  updateRow,
}: {
  row: OverrideRow
  rtId: number
  disabled: boolean
  updateRow: (id: number, patch: Partial<OverrideRow>) => void
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          <Switch className="scale-75 origin-left" disabled={disabled} checked={row.stopSell} onCheckedChange={(v) => updateRow(rtId, { stopSell: v })} id={`ss-${rtId}`} />
          <Tooltip><TooltipTrigger asChild><Label htmlFor={`ss-${rtId}`} className="text-[11px] text-muted-foreground cursor-pointer">SS</Label></TooltipTrigger><TooltipContent side="bottom">Stop Sell</TooltipContent></Tooltip>
        </div>
        <div className="flex items-center gap-1.5">
          <Switch className="scale-75 origin-left" disabled={disabled} checked={row.closedToArrival} onCheckedChange={(v) => updateRow(rtId, { closedToArrival: v })} id={`cta-${rtId}`} />
          <Tooltip><TooltipTrigger asChild><Label htmlFor={`cta-${rtId}`} className="text-[11px] text-muted-foreground cursor-pointer">CTA</Label></TooltipTrigger><TooltipContent side="bottom">Closed to Arrival</TooltipContent></Tooltip>
        </div>
        <div className="flex items-center gap-1.5">
          <Switch className="scale-75 origin-left" disabled={disabled} checked={row.closedToDeparture} onCheckedChange={(v) => updateRow(rtId, { closedToDeparture: v })} id={`ctd-${rtId}`} />
          <Tooltip><TooltipTrigger asChild><Label htmlFor={`ctd-${rtId}`} className="text-[11px] text-muted-foreground cursor-pointer">CTD</Label></TooltipTrigger><TooltipContent side="bottom">Closed to Departure</TooltipContent></Tooltip>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1">
          <Label htmlFor={`min-${rtId}`} className="text-[11px] text-muted-foreground whitespace-nowrap">Min</Label>
          <Input id={`min-${rtId}`} type="number" min="1" disabled={disabled} className="w-16 h-7 text-xs text-right tabular-nums" placeholder="--" value={row.minStayArrival} onChange={(e) => updateRow(rtId, { minStayArrival: e.target.value })} />
        </div>
        <div className="flex items-center gap-1">
          <Label htmlFor={`max-${rtId}`} className="text-[11px] text-muted-foreground whitespace-nowrap">Max</Label>
          <Input id={`max-${rtId}`} type="number" min="1" disabled={disabled} className="w-16 h-7 text-xs text-right tabular-nums" placeholder="--" value={row.maxStay} onChange={(e) => updateRow(rtId, { maxStay: e.target.value })} />
        </div>
      </div>
    </div>
  )
}

export default function DayDetailEditPrices({
  date,
  roomTypes,
  rates,
  currency,
  propertyBasePath,
  isPastDate,
  today,
  onDirtyChange,
  onSaved,
  mixedPlans,
  pageIdentity,
}: DayDetailEditPricesProps) {
  const sym = currencySymbol(currency)

  // Same flat ladder as Rooms Setup and the Overview tab: a room is drawn under the room it actually
  // follows, whichever hotel that lives in, and the indent plus the curved arrow say so unaided.
  const ladder = useMemo(
    () => nestRoomRows(roomTypes, (rt) => rt.parentRoomTypeId ?? null, { crossHotelLinks: 'nest' }),
    [roomTypes],
  )
  // Rows are named by the server's building name for each room (a name two listings share carries its
  // listing in brackets), so no row wears a hotel label (owner ruling 2026-10-26).

  const [rows, setRows] = useState<Record<number, OverrideRow>>(() =>
    buildInitialRows(roomTypes, rates, date)
  )
  const [startDate, setStartDate] = useState(date)
  const [endDate, setEndDate] = useState(date)
  const [batchReason, setBatchReason] = useState('')
  const [headerPct, setHeaderPct] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<number, string[]>>({})
  const initialRowsRef = useRef<Record<number, OverrideRow>>(
    buildInitialRows(roomTypes, rates, date)
  )

  // Debounced-live impact preview (backend-computed, display only)
  const [preview, setPreview] = useState<OverridePreviewResponse | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const previewAbortRef = useRef<AbortController | null>(null)

  // Review step
  const [reviewOpen, setReviewOpen] = useState(false)
  const [reviewClearing, setReviewClearing] = useState(false)
  const [clearRestrictions, setClearRestrictions] = useState(false)
  const [releaseOn, setReleaseOn] = useState('')

  // What overrides ALREADY exist in the selected span — drives the one-click
  // clear and the count in its confirmation.
  const [scope, setScope] = useState<OverrideScope | null>(null)
  const [clearOpen, setClearOpen] = useState(false)
  const scopeAbortRef = useRef<AbortController | null>(null)

  // Post-save recompute state
  const [awaiting, setAwaiting] = useState<{ rtIds: number[]; date: string } | null>(null)
  const awaitingRef = useRef(awaiting)
  awaitingRef.current = awaiting
  const awaitingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [justUpdated, setJustUpdated] = useState(false)
  // The batch endpoints answer `{ notice }`, the server's sentence naming the hotel (R1.10). It is
  // the inline status while the save recomputes, in place of a sentence of our own.
  const [savedNotice, setSavedNotice] = useState<string | null>(null)
  const justUpdatedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // True when the span covers more than the open day. Only affects wording now —
  // EVERY save goes through review, one day included, because the release date
  // and the guardrail/replacement counts matter just as much for a single night.
  const isRange = endDate > startDate

  // Calendar arithmetic over the selected span, clamped to today. Used by the
  // review header and by the clear path (which has no drafted rooms and so no
  // server-side range). Counts days, never money — see lib/date-span.
  const fallbackSpan = useMemo(
    () => resolveDateSpan(startDate, endDate, today),
    [startDate, endDate, today]
  )

  useEffect(() => {
    const fresh = buildInitialRows(roomTypes, rates, date)
    setRows(fresh)
    initialRowsRef.current = fresh
    setStartDate(date)
    setEndDate(date)
    setBatchReason('')
    setHeaderPct(null)
    setErrors({})
    setSaving(false)
    setPreview(null)
    setPreviewLoading(false)
    setReviewOpen(false)
    setReleaseOn('')
    setClearRestrictions(false)

    const pending = awaitingRef.current
    if (pending) {
      setAwaiting(null)
      if (awaitingTimeoutRef.current) clearTimeout(awaitingTimeoutRef.current)
      if (pending.date === date) {
        setJustUpdated(true)
        if (justUpdatedTimeoutRef.current) clearTimeout(justUpdatedTimeoutRef.current)
        justUpdatedTimeoutRef.current = setTimeout(() => setJustUpdated(false), 4000)
      }
    }
  }, [date, roomTypes, rates])

  useEffect(() => {
    return () => {
      previewAbortRef.current?.abort()
      if (awaitingTimeoutRef.current) clearTimeout(awaitingTimeoutRef.current)
      if (justUpdatedTimeoutRef.current) clearTimeout(justUpdatedTimeoutRef.current)
    }
  }, [])

  // Which rooms differ from what was loaded — computed once per change rather
  // than re-serialised per room on every render and again inside the save loop.
  const dirtyIds = useMemo(() => {
    const ids = new Set<number>()
    for (const rt of roomTypes) {
      const current = rows[rt.id]
      const initial = initialRowsRef.current[rt.id]
      if (!current || !initial) continue
      if (JSON.stringify(current) !== JSON.stringify(initial)) ids.add(rt.id)
    }
    return ids
  }, [rows, roomTypes])

  const hasChanges = dirtyIds.size > 0

  const prevHasChangesRef = useRef(hasChanges)
  useEffect(() => {
    if (prevHasChangesRef.current !== hasChanges) {
      prevHasChangesRef.current = hasChanges
      onDirtyChange(hasChanges)
    }
  }, [hasChanges, onDirtyChange])

  const isRowDirty = useCallback((rtId: number): boolean => dirtyIds.has(rtId), [dirtyIds])

  const updateRow = useCallback((rtId: number, patch: Partial<OverrideRow>) => {
    setRows((prev) => ({ ...prev, [rtId]: { ...prev[rtId], ...patch } }))
  }, [])

  // ─── Room classification ────────────────────────────────────────────────────
  const currentRateOf = useCallback(
    (rtId: number): number | null => rates[`${rtId}:${date}`]?.rateAmount ?? null,
    [rates, date]
  )

  /** The engine's own price for this day, before any override — the number a
   *  percentage multiplies, and what the Computed column shows.
   *
   *  Resolved once per room when the rates change, not on every keystroke:
   *  walking the adjustment trace is the same work for every render, and the
   *  answer only moves when the backend sends new rates. */
  const computedRates = useMemo(() => {
    const map: Record<number, number | null> = {}
    for (const rt of roomTypes) {
      const rate = rates[`${rt.id}:${date}`]
      const adjs = rate?.computationDetails?.adjustments ?? []
      if (adjs.length === 0) {
        map[rt.id] = rate?.rateAmount ?? null
        continue
      }
      map[rt.id] = engineComputedTotal(computeRunningTotals(adjs, sym)) ?? rate?.rateAmount ?? null
    }
    return map
  }, [roomTypes, rates, date, sym])

  const computedRateOf = useCallback((rtId: number): number | null => computedRates[rtId] ?? null, [computedRates])

  const rowHasManualRate = useCallback(
    (rtId: number): boolean => {
      const row = rows[rtId]
      const initial = initialRowsRef.current[rtId]
      return !!row && row.pct === '' && row.rateAmount !== '' && row.rateAmount !== initial?.rateAmount
    },
    [rows]
  )

  const rowHasPct = useCallback(
    (rtId: number): boolean => {
      const row = rows[rtId]
      const initial = initialRowsRef.current[rtId]
      return !!row && row.pct !== '' && row.pct !== initial?.pct && !Number.isNaN(parseFloat(row.pct))
    },
    [rows]
  )

  /** The percentage currently sitting in this child's lead row, if any. */
  const leadPctOf = useCallback(
    (rt: RoomType): string => (rt.parentRoomTypeId != null ? (rows[rt.parentRoomTypeId]?.pct ?? '') : ''),
    [rows]
  )

  // A child showing exactly its lead's percentage is NOT priced separately — the
  // parent link already carries that move through to it, so the value in the
  // field is a readout of what is about to happen, not a second instruction.
  // Writing a child override here would duplicate the link and quietly break the
  // hierarchy the first time the offset changed.
  const childMirrorsLead = useCallback(
    (rt: RoomType): boolean => {
      const pct = rows[rt.id]?.pct ?? ''
      const lead = leadPctOf(rt)
      return pct !== '' && pct === lead
    },
    [rows, leadPctOf]
  )

  const isDetachedChild = useCallback(
    (rt: RoomType): boolean => {
      if (rt.parentRoomTypeId == null) return false
      const row = rows[rt.id]
      if (!row || row.clearOverride) return false
      const rate = rates[`${rt.id}:${date}`]
      if (rate?.source === 'manual_override' || rowHasManualRate(rt.id)) return true
      return rowHasPct(rt.id) && !childMirrorsLead(rt)
    },
    [rows, rates, date, rowHasManualRate, rowHasPct, childMirrorsLead]
  )

  const isFollowingChild = useCallback(
    (rt: RoomType): boolean => rt.parentRoomTypeId != null && !isDetachedChild(rt),
    [isDetachedChild]
  )

  // In-progress drafts. A row contributes EITHER a percentage or a typed price —
  // never both, and never a following child. A child mirroring its lead's
  // percentage counts as following: the value is on screen so the fan-out is
  // visible, but the parent link is what delivers it, so drafting (and later
  // writing) it would create a second, competing instruction.
  const draftEntries = useMemo(() => {
    const drafts: DraftEntry[] = []
    for (const rt of roomTypes) {
      const row = rows[rt.id]
      if (!row) continue
      if (row.clearOverride) {
        // A removal previews like every other intent: the server answers what the
        // room (and its followers) land on once the override is gone.
        if (rates[`${rt.id}:${date}`]?.source === 'manual_override') drafts.push({ room_type_id: rt.id, remove: true })
        continue
      }
      if (isFollowingChild(rt)) continue
      if (rowHasPct(rt.id)) {
        drafts.push({ room_type_id: rt.id, adjust: { type: 'percentage', value: parseFloat(row.pct) } })
      } else if (rowHasManualRate(rt.id)) {
        drafts.push({ room_type_id: rt.id, rate_amount: row.rateAmount })
      }
    }
    return drafts
  }, [rows, roomTypes, rates, date, rowHasManualRate, rowHasPct, isFollowingChild])

  // `rows` changes on every keystroke anywhere in the form — a min stay, a
  // stop-sell toggle, a cleared flag — but only a price or a percentage alters
  // what the preview has to resolve. Keying the request on the drafts' own
  // CONTENTS stops a restriction edit re-running a whole range preview on the
  // server; the ref hands the effect the current drafts without putting their
  // (always-new) array identity in its dependencies.
  const draftKey = useMemo(() => JSON.stringify(draftEntries), [draftEntries])
  const draftsRef = useRef(draftEntries)
  draftsRef.current = draftEntries

  // Debounced impact preview. When the span covers more than the open day the
  // request also carries it, so the review step has its data ready the moment
  // the operator asks for it. Every value returned is DISPLAY ONLY.
  useEffect(() => {
    if (isPastDate || awaiting) return
    if (draftsRef.current.length === 0) {
      previewAbortRef.current?.abort()
      setPreview(null)
      setPreviewLoading(false)
      return
    }

    setPreviewLoading(true)
    const timer = setTimeout(async () => {
      previewAbortRef.current?.abort()
      const controller = new AbortController()
      previewAbortRef.current = controller
      try {
        const response = await apiFetch(`${propertyBasePath}/overrides/preview`, {
          method: 'POST',
          // The span always travels with the request: review runs on every save,
          // so it always needs per-day figures, even for a span of one.
          body: JSON.stringify({
            date,
            draft_overrides: draftsRef.current,
            start_date: startDate,
            end_date: endDate,
          }),
          signal: controller.signal,
        })
        if (controller.signal.aborted) return
        if (response.ok) {
          const data = (await response.json()) as OverridePreviewResponse
          if (!controller.signal.aborted) setPreview(data)
        } else {
          setPreview(null)
        }
      } catch {
        if (controller.signal.aborted) return
        setPreview(null)
      } finally {
        if (previewAbortRef.current === controller && !controller.signal.aborted) {
          setPreviewLoading(false)
        }
      }
    }, PREVIEW_DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [draftKey, date, startDate, endDate, propertyBasePath, isPastDate, awaiting])

  // Census of existing overrides in the span. Refetched when the span moves or
  // after a save lands, so the clear button never offers a stale count.
  useEffect(() => {
    if (isPastDate) return
    scopeAbortRef.current?.abort()
    const controller = new AbortController()
    scopeAbortRef.current = controller

    const timer = setTimeout(async () => {
      try {
        const response = await apiFetch(
          `${propertyBasePath}/overrides/scope?start_date=${startDate}&end_date=${endDate}`,
          { method: 'GET', signal: controller.signal }
        )
        if (controller.signal.aborted) return
        setScope(response.ok ? ((await response.json()) as OverrideScope) : null)
      } catch {
        if (!controller.signal.aborted) setScope(null)
      }
    }, 250)

    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [propertyBasePath, startDate, endDate, isPastDate, rates])

  const previewDrafted = useMemo(() => {
    const map: Record<number, OverridePreviewDrafted> = {}
    if (!preview || preview.date !== date) return map
    for (const d of preview.drafted) map[d.room_type_id] = d
    return map
  }, [preview, date])

  const previewChildren = useMemo(() => {
    const map: Record<number, OverridePreviewChild & { parentName: string }> = {}
    if (!preview || preview.date !== date) return map
    for (const d of preview.drafted) {
      for (const c of d.child_summaries) map[c.room_type_id] = { ...c, parentName: d.name }
    }
    return map
  }, [preview, date])

  // Combined inventory: Same rooms that move with this change (server-counted). A Same room has no row
  // in this form (it takes no manual price), so the footer is where the operator learns it moves too.
  const sameRoomsFollowing = preview && preview.date === date ? preview.counts.same_rooms_following ?? 0 : 0

  const rangePreview: OverrideRangePreview | null = useMemo(
    () => (preview && preview.date === date ? (preview.range ?? null) : null),
    [preview, date]
  )

  const resolvedPctAmount = useCallback(
    (rtId: number): number | null => previewDrafted[rtId]?.applied_amount ?? null,
    [previewDrafted]
  )

  // ─── Intent handlers ────────────────────────────────────────────────────────

  // Fan a percentage across EVERY editable room, linked children included.
  //
  // "Put everything up 10%" is one instruction, so every room shows 10 — a child
  // included in the move must not look untouched. The value in a child's field is
  // a readout, not a duplicate write: while it matches its lead, the parent link
  // still delivers the move and no child override is created. Change it and the
  // child detaches with its own percentage; clear it and it goes back to plain
  // following (which, with the lead moving, still carries it along).
  const applyHeaderPct = useCallback(
    (pct: number) => {
      const targetIds = roomTypes
        .filter((rt) => currentRateOf(rt.id) != null && !rows[rt.id]?.clearOverride)
        .map((rt) => rt.id)
      if (targetIds.length === 0) return
      setRows((prev) => {
        const next = { ...prev }
        for (const id of targetIds) next[id] = { ...prev[id], pct: String(pct), rateAmount: '' }
        return next
      })
      setHeaderPct(pct)
    },
    [roomTypes, currentRateOf, rows]
  )

  // Typing a percentage into a LEAD carries it down to every child that is still
  // following, so the fan-out reads the same whether it came from the strip or
  // from one row. Children that already hold their own price are left alone.
  const applyLeadPct = useCallback(
    (rtId: number, value: string, removing = false) => {
      setRows((prev) => {
        const next = { ...prev, [rtId]: { ...prev[rtId], pct: value, rateAmount: '', clearOverride: removing } }
        for (const child of roomTypes) {
          if (child.parentRoomTypeId !== rtId) continue
          const row = prev[child.id]
          if (!row || row.clearOverride) continue
          // Leave a child that has been given its own price or its own differing %.
          const ownPrice = row.rateAmount !== '' || rates[`${child.id}:${date}`]?.source === 'manual_override'
          const ownPct = row.pct !== '' && row.pct !== (prev[rtId]?.pct ?? '')
          if (ownPrice || ownPct) continue
          next[child.id] = { ...row, pct: value, rateAmount: '' }
        }
        return next
      })
      setHeaderPct(null)
    },
    [roomTypes, rates, date]
  )

  /**
   * Hold a room at the price it is on today, so a move happening around it does
   * not carry it along.
   *
   * This is what emptying a linked child's field means once its lead is moving.
   * Returning it to plain following would be the opposite of the intent — the
   * lead's uplift would still reach it — so the room is pinned at its current
   * figure instead. That pin is an ordinary fixed override, which is exactly what
   * "leave this one where it is" means to the engine.
   */
  const holdAtCurrent = useCallback(
    (rtId: number) => {
      const current = currentRateOf(rtId)
      setRows((prev) => ({
        ...prev,
        [rtId]: {
          ...prev[rtId],
          pct: '',
          rateAmount: current != null ? String(current) : '',
          clearOverride: false,
        },
      }))
      setHeaderPct(null)
    },
    [currentRateOf]
  )

  /** Drop this row's pending edit and go back to whatever it was before. */
  const revertRow = useCallback((rtId: number) => {
    setRows((prev) => ({ ...prev, [rtId]: { ...initialRowsRef.current[rtId] } }))
    setHeaderPct(null)
  }, [])

  // Typing a percentage. A lead fans its value down to its followers; on a child
  // a value differing from its lead's IS the detach. Emptying the field while the
  // lead is moving means "leave this room alone", which is a hold, not a revert.
  //
  // Emptying BOTH inputs on a room that has a saved override is the third intent —
  // remove it, back to the engine — and is shown as exactly that. It used to fall
  // through as a "fixed price" with no amount, which the server stored and the
  // engine ignored: a silent removal wearing an Override badge.
  const onPctChange = useCallback(
    (rtId: number, value: string) => {
      const rt = roomTypes.find((r) => r.id === rtId)
      const removing = value === '' && rates[`${rtId}:${date}`]?.source === 'manual_override'
      if (rt && rt.childCount > 0) {
        applyLeadPct(rtId, value, removing)
        return
      }
      if (value === '' && rt?.parentRoomTypeId != null && (rows[rt.parentRoomTypeId]?.pct ?? '') !== '') {
        holdAtCurrent(rtId)
        return
      }
      setRows((prev) => ({ ...prev, [rtId]: { ...prev[rtId], pct: value, rateAmount: '', clearOverride: removing } }))
      setHeaderPct(null)
    },
    [roomTypes, rows, rates, date, applyLeadPct, holdAtCurrent]
  )

  // Typing a price. Clears any percentage — the column you touch is the mode.
  // Emptying it on a room a lead is moving holds it where it is, for the same
  // reason as the % column.
  const onRateManual = useCallback(
    (rtId: number, value: string) => {
      const rt = roomTypes.find((r) => r.id === rtId)
      if (value === '' && rt?.parentRoomTypeId != null && (rows[rt.parentRoomTypeId]?.pct ?? '') !== '') {
        holdAtCurrent(rtId)
        return
      }
      // Same rule as the % column: both inputs empty on an overridden room means remove it.
      const removing = value === '' && rates[`${rtId}:${date}`]?.source === 'manual_override'
      setRows((prev) => ({ ...prev, [rtId]: { ...prev[rtId], rateAmount: value, pct: '', clearOverride: removing } }))
      setHeaderPct(null)
    },
    [roomTypes, rows, rates, date, holdAtCurrent]
  )

  const relinkChild = useCallback(
    (rtId: number) => {
      const hadSavedOverride = rates[`${rtId}:${date}`]?.source === 'manual_override'
      setRows((prev) => ({
        ...prev,
        [rtId]: { ...prev[rtId], pct: '', rateAmount: '', clearOverride: hadSavedOverride },
      }))
      setHeaderPct(null)
    },
    [rates, date]
  )

  const handleDiscard = useCallback(() => {
    const fresh = buildInitialRows(roomTypes, rates, date)
    setRows(fresh)
    initialRowsRef.current = fresh
    setStartDate(date)
    setEndDate(date)
    setBatchReason('')
    setHeaderPct(null)
    setErrors({})
    setPreview(null)
    setReleaseOn('')
  }, [roomTypes, rates, date])

  const summary = useMemo(() => {
    const writes = roomTypes.filter((rt) => {
      const row = rows[rt.id]
      if (!row || row.clearOverride || !isRowDirty(rt.id)) return false
      if (isFollowingChild(rt)) return false
      if (rowHasPct(rt.id)) return resolvedPctAmount(rt.id) != null
      if (rowHasManualRate(rt.id)) return true
      // A restriction-only edit is still a write — the footer must never say
      // "no changes" while Review is enabled.
      return deriveOverrideType(row) !== 'rate'
    })
    const leadWrites = writes.filter((rt) => rt.childCount > 0).length
    const detachedWrites = writes.filter((rt) => rt.parentRoomTypeId != null).length
    return {
      count: writes.length,
      leadWrites,
      indepWrites: writes.length - leadWrites - detachedWrites,
      detachedWrites,
      follows: Object.keys(previewChildren).length,
      capped:
        writes.filter((rt) => previewDrafted[rt.id]?.clamped).length +
        Object.values(previewChildren).filter((c) => c.clamped).length,
    }
  }, [roomTypes, rows, previewDrafted, previewChildren, resolvedPctAmount, rowHasManualRate, rowHasPct, isRowDirty, isFollowingChild])

  // Rows scheduled for a price clear.
  const clearTargets = useMemo(
    () => roomTypes.filter((rt) => rows[rt.id]?.clearOverride),
    [roomTypes, rows]
  )

  // Save waits only on rows that are actually being resolved server-side. A
  // mirrored child is never drafted, so waiting on it would block Save forever.
  const hasPendingPct = useMemo(() => {
    const pctRows = roomTypes.filter(
      (rt) => rowHasPct(rt.id) && !rows[rt.id]?.clearOverride && !isFollowingChild(rt)
    )
    if (pctRows.length === 0) return false
    return previewLoading || pctRows.some((rt) => resolvedPctAmount(rt.id) == null)
  }, [roomTypes, rows, previewLoading, resolvedPctAmount, rowHasPct, isFollowingChild])

  // ─── Save ───────────────────────────────────────────────────────────────────

  const commit = useCallback(async () => {
    setSaving(true)
    setErrors({})

    const overrides: Record<string, unknown>[] = []
    const deletions: Record<string, unknown>[] = []
    const rtIdToIndex: Record<number, number> = {}
    const savedRtIds: number[] = []
    const trimmedReason = batchReason.trim()

    for (const rt of roomTypes) {
      if (!isRowDirty(rt.id)) continue

      const row = rows[rt.id]

      // A child mirroring its lead's percentage looks changed (its field holds a
      // value) but must not be written: the parent link already carries the move,
      // and a child override would compete with it.
      if (!row.clearOverride && isFollowingChild(rt)) continue

      savedRtIds.push(rt.id)

      if (row.clearOverride) {
        deletions.push({ room_type_id: rt.id, start_date: startDate, end_date: endDate })
        continue
      }

      const relative = rowHasPct(rt.id)
      // Belt and braces for the server's rule: a fixed row with no amount and no
      // restriction is not an instruction. If a saved override exists it is a removal;
      // otherwise there is nothing to write.
      if (!relative && row.rateAmount === '' && deriveOverrideType(row) === 'rate') {
        if (rates[`${rt.id}:${date}`]?.source === 'manual_override') {
          deletions.push({ room_type_id: rt.id, start_date: startDate, end_date: endDate })
        }
        continue
      }
      const idx = overrides.length
      rtIdToIndex[rt.id] = idx

      overrides.push({
        room_type_id: rt.id,
        start_date: startDate,
        end_date: endDate,
        override_type: deriveOverrideType(row),
        // A relative row sends its PERCENTAGE and no amount: the engine re-derives
        // each day from that day's own computed rate. Sending a resolved figure
        // here is exactly what used to flatten a range.
        mode: relative ? 'relative' : 'fixed',
        adjust_type: relative ? 'percentage' : null,
        adjust_value: relative ? row.pct : null,
        rate_amount: relative ? null : row.rateAmount || null,
        release_on: releaseOn || null,
        reason: (trimmedReason !== '' ? trimmedReason : row.reason) || null,
        stop_sell: row.stopSell,
        closed_to_arrival: row.closedToArrival,
        closed_to_departure: row.closedToDeparture,
        // Never persist a one-night "minimum" — it is not a restriction, and
        // writing it turns a price override into override_type 'both'.
        min_stay_arrival: meaningfulMinStay(Number(row.minStayArrival)) || null,
        max_stay: row.maxStay || null,
      })
    }

    if (overrides.length === 0 && deletions.length === 0) {
      setSaving(false)
      return
    }

    try {
      // A commit that clears and then writes sends two requests; the last answer's notice is the one
      // shown (spec §5.10.4: that is the commit's shape).
      let notice: string | null = null
      if (deletions.length > 0) {
        const response = await apiFetch(`${propertyBasePath}/overrides/batch`, {
          method: 'DELETE',
          body: JSON.stringify({ deletions, clear_restrictions: clearRestrictions }),
        })
        notice = (await readNotice(response)) ?? notice
      }

      if (overrides.length > 0) {
        const response = await apiFetch(`${propertyBasePath}/overrides/batch`, {
          method: 'POST',
          body: JSON.stringify({ overrides }),
        })

        if (response.status === 422) {
          const data = await response.json()
          const mappedErrors: Record<number, string[]> = {}
          if (data.errors) {
            for (const [idxStr, msgs] of Object.entries(data.errors)) {
              const idx = parseInt(idxStr, 10)
              const rtId = Object.entries(rtIdToIndex).find(([, v]) => v === idx)?.[0]
              if (rtId) mappedErrors[parseInt(rtId, 10)] = msgs as string[]
            }
          }
          setErrors(mappedErrors)
          setSaving(false)
          setReviewOpen(false)
          return
        }
        notice = (await readNotice(response)) ?? notice
      }

      setSavedNotice(notice)
      markRecomputeExpected()
      previewAbortRef.current?.abort()
      setPreview(null)
      setPreviewLoading(false)
      initialRowsRef.current = JSON.parse(JSON.stringify(rows))
      setBatchReason('')
      setHeaderPct(null)
      setSaving(false)
      setReviewOpen(false)
      setAwaiting({ rtIds: savedRtIds, date })
      if (awaitingTimeoutRef.current) clearTimeout(awaitingTimeoutRef.current)
      awaitingTimeoutRef.current = setTimeout(() => setAwaiting(null), RECOMPUTE_TIMEOUT_MS)
      onSaved()
    } catch (e) {
      setErrors({ 0: [apiErrorMessage(e, 'Network error. Please try again.')] })
      setSaving(false)
      setReviewOpen(false)
    }
  }, [
    roomTypes, rows, rates, startDate, endDate, date, propertyBasePath, batchReason, isRowDirty,
    rowHasPct, isFollowingChild, releaseOn, clearRestrictions, onSaved,
  ])

  // EVERY save goes through review — one day or ninety. A single night still
  // gets a release date, still replaces whatever was there, and can still land
  // on a guardrail; none of that should be invisible just because the span is
  // short. The review is also the only place the release date is offered.
  const handleSave = useCallback(() => {
    setReviewClearing(clearTargets.length > 0 && summary.count === 0)
    setReviewOpen(true)
  }, [clearTargets.length, summary.count])

  /**
   * Remove every existing manual price in the span, in one action.
   *
   * Independent of the edit form: it deletes what is already saved rather than
   * committing anything drafted, so it does not read `rows` at all and cannot be
   * confused by a half-finished edit sitting on screen.
   */
  const commitClearAll = useCallback(async () => {
    if (!scope || scope.total_overrides === 0) return
    setSaving(true)
    setErrors({})

    const deletions = scope.rooms.map((r) => ({
      room_type_id: r.room_type_id,
      start_date: scope.start_date,
      end_date: scope.end_date,
    }))

    try {
      const response = await apiFetch(`${propertyBasePath}/overrides/batch`, {
        method: 'DELETE',
        body: JSON.stringify({ deletions, clear_restrictions: clearRestrictions }),
      })

      setSavedNotice(await readNotice(response))
      markRecomputeExpected()
      previewAbortRef.current?.abort()
      setPreview(null)
      setPreviewLoading(false)
      setSaving(false)
      setClearOpen(false)
      setClearRestrictions(false)
      setScope(null)
      setAwaiting({ rtIds: scope.rooms.map((r) => r.room_type_id), date })
      if (awaitingTimeoutRef.current) clearTimeout(awaitingTimeoutRef.current)
      awaitingTimeoutRef.current = setTimeout(() => setAwaiting(null), RECOMPUTE_TIMEOUT_MS)
      onSaved()
    } catch (e) {
      setErrors({ 0: [apiErrorMessage(e, 'Network error. Please try again.')] })
      setSaving(false)
      setClearOpen(false)
    }
  }, [scope, propertyBasePath, clearRestrictions, date, onSaved])

  if (isPastDate) {
    return (
      <div className="animate-slide-in m-6 rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm">
        <CalendarOff className="mx-auto h-8 w-8 text-muted-foreground/40" />
        <p className="mt-3 text-sm font-medium text-foreground">Cannot edit past dates</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Switch to the Overview tab to review historical data.
        </p>
      </div>
    )
  }

  const isAwaiting = awaiting != null
  const canSave = hasChanges && !saving && !isAwaiting && !hasPendingPct

  return (
    <TooltipProvider delayDuration={200}>
    {/* Three bands: the controls that scope the edit, a scrolling room list, and
        a pinned action bar. Only the middle one moves, so the date range, the
        adjust chips and the Save/Discard buttons are always reachable however
        many rooms the property has. */}
    <div className="flex h-full min-h-0 flex-col">
      {/* Date range */}
      <div className="animate-slide-in grid shrink-0 grid-cols-2 gap-4 px-6 pt-5" style={{ animationDelay: '0ms' }}>
        <div className="space-y-2">
          <Label htmlFor="edit-start-date">From</Label>
          <Input id="edit-start-date" type="date" min={today} value={startDate} disabled={isAwaiting} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="edit-end-date">To</Label>
          <Input id="edit-end-date" type="date" min={startDate} value={endDate} disabled={isAwaiting} onChange={(e) => setEndDate(e.target.value)} />
        </div>
      </div>

      {/* All-rooms adjust — one inline row. As a filled card it read louder than
          the rooms it acts on, and cost a block of height to say very little. */}
      <div className="animate-slide-in mt-4 flex shrink-0 flex-wrap items-center gap-x-2 gap-y-2 px-6" style={{ animationDelay: '40ms' }}>
        <span className="text-xs font-medium text-muted-foreground">Adjust all</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" className="text-muted-foreground/60 transition-colors hover:text-muted-foreground" aria-label="How bulk adjust works">
              <Info className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-[300px] text-xs">
            A percentage is applied to <strong>each day’s own</strong> computed price, so a date range keeps its
            shape. A fixed price sets every day to the same number. Both are worked out on the server and capped by
            each room’s guardrails. Linked rooms move with their lead.
          </TooltipContent>
        </Tooltip>
        <div className="flex flex-wrap items-center gap-1">
          {NEG_CHIPS.map((p) => (
            <ChipButton key={p} pct={p} selected={headerPct === p} onPick={applyHeaderPct} />
          ))}
          <span className="mx-1 hidden h-5 w-px bg-border sm:block" aria-hidden="true" />
          {POS_CHIPS.map((p) => (
            <ChipButton key={p} pct={p} selected={headerPct === p} onPick={applyHeaderPct} />
          ))}
        </div>
        <div className="ml-auto flex items-center gap-1">
          {/* One click to remove every manual price already in the span. Shown
              only when there is something to remove, so it never sits there as
              a dead control. */}
          {scope != null && scope.total_overrides > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 rounded-lg px-2 text-xs text-rose-500 hover:bg-rose-50 hover:text-rose-600"
              onClick={() => setClearOpen(true)}
              disabled={isAwaiting || saving}
            >
              <Trash2 className="mr-1 h-3 w-3" />
              Clear {scope.total_overrides} manual {scope.total_overrides === 1 ? 'price' : 'prices'}
            </Button>
          )}
        </div>
      </div>

      {/* Column headings. Now and Engine are readouts; the two inputs ARE the mode
          selector, so they get named. "After" is what the room sells at once this
          edit is saved — pre-filled from the stored rule so the current instruction
          is always visible and editable. */}
      <div className="animate-slide-in mt-4 shrink-0 px-6" style={{ animationDelay: '50ms' }}>
        <div className="flex items-end gap-6">
          <div className="flex-1 min-w-0" />
          <div className="flex w-[384px] shrink-0 items-center gap-2">
            <span className="w-[72px] text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Now
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="w-[72px] cursor-default text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Engine
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[260px] text-xs">
                What the engine would charge with no override. A percentage is applied to this figure.
              </TooltipContent>
            </Tooltip>
            <span className="w-[92px] text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              %
            </span>
            <span className="w-[96px] text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              After
            </span>
            <span className="w-5 shrink-0" />
          </div>
          <div className="w-[52px] shrink-0 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Rules
          </div>
        </div>
      </div>

      {/* Room types — the only band that scrolls */}
      <div
        className="animate-slide-in mt-2 min-h-0 flex-1 divide-y divide-border/40 overflow-y-auto border-y border-border/60 bg-white"
        style={{ animationDelay: '60ms' }}
      >
        {ladder.map(({ row: rt, depth }) => {
          const rate = rates[`${rt.id}:${date}`]
          const row = rows[rt.id]
          if (!row) return null

          // FOLLOWS a lead room — true wherever that lead lives. Drives the controls: whether the
          // row can be detached, whether it resumes following, whether it shows an offset.
          const isChild = rt.parentRoomTypeId != null
          // DRAWN under its lead. Equal to isChild whenever the lead is in this list, which after
          // nesting is the normal case — it stays separate only so a room whose lead was filtered
          // out still renders flat instead of indented under nothing.
          const isNested = depth > 0
          const isParent = rt.childCount > 0
          const isOverride = rate?.source === 'manual_override'
          const dirty = isRowDirty(rt.id)
          const rowErrors = errors[rt.id]
          const rowAwaiting = isAwaiting && awaiting.rtIds.includes(rt.id)

          const detached = isDetachedChild(rt)
          const following = isChild && !detached
          const leadPct = leadPctOf(rt)
          const mirrorsLead = following && childMirrorsLead(rt)
          // The row's MODE: any percentage in the % column, saved or pending.
          const inRelativeMode = row.pct !== '' && !Number.isNaN(parseFloat(row.pct))
          // Whether that percentage is a PENDING edit (what gets previewed/saved).
          const hasPct = rowHasPct(rt.id)
          const dirtyRate = rowHasManualRate(rt.id)

          const draftedPreview = previewDrafted[rt.id]
          const pctApplied = hasPct ? resolvedPctAmount(rt.id) : null
          const pctResolving = hasPct && pctApplied == null

          const followPreview = following ? previewChildren[rt.id] : undefined
          const followValue = following ? followPreview?.would_become_amount ?? rate?.rateAmount ?? null : null

          const computedRate = computedRateOf(rt.id)
          const currentRate = rate?.rateAmount ?? null
          const storedRule = storedRuleOf(rt, rate)
          // The base a pending % multiplies — the server's figure once the preview lands,
          // the row's own trace until then. Same number by construction (pre_override_amount).
          const engineAmount = draftedPreview?.engine_amount ?? computedRate
          // Where the price stands with the PMS, read off the data rather than the stale
          // sync column: a recomputed row keeps "synced" until the next push lands.
          const pmsState: 'never' | 'synced' | 'pending' =
            rate == null || rate.pushedRateAmount == null
              ? 'never'
              : rate.pushedRateAmount === rate.rateAmount
                ? 'synced'
                : 'pending'

          const typedVal = !hasPct && row.rateAmount ? parseFloat(row.rateAmount) : null
          const manualPreview = dirtyRate ? draftedPreview : undefined
          const appliedDiffers =
            manualPreview != null && typedVal != null && manualPreview.applied_amount !== typedVal
          const aboveCeiling = typedVal != null && rt.maxRate != null && typedVal > rt.maxRate
          const belowFloor = typedVal != null && rt.minRate != null && typedVal < rt.minRate

          // The Price column is DERIVED whenever the row's instruction lives
          // elsewhere — in a percentage, or in a parent link. It still accepts
          // typing: that is precisely how the operator switches such a row back
          // to a fixed price (and how a following child detaches). Making it
          // read-only would leave a relative row with no way out.
          //
          // Pending % → the backend figure for the OPEN day. Saved % → the rate
          // already on the day. Following child → its follow value. Otherwise the
          // operator's own text. Every branch is display-only.
          //
          // `following` is checked FIRST: a mirrored child holds a percentage in
          // its field, but its price still arrives through the parent link, so
          // the follow value is the right number to show. Reading the relative
          // branch instead would leave it waiting on a draft that is never sent.
          const priceIsDerived = inRelativeMode || following
          const priceValue = following
            ? followValue != null
              ? String(followValue)
              : ''
            : hasPct
              ? pctApplied != null
                ? String(pctApplied)
                : ''
              : inRelativeMode
                ? currentRate != null
                  ? String(currentRate)
                  : ''
                : row.rateAmount

          return (
            <div
              key={rt.id}
              className={cn(
                'border-l-2 border-l-transparent px-6 py-3 transition-colors',
                isNested && 'py-2.5 bg-slate-50/40',
                dirty && 'border-l-primary',
                rowErrors && 'bg-rose-50/50',
                rowAwaiting && 'animate-pulse bg-primary/[0.04]'
              )}
            >
              <div className={cn('flex items-start gap-6', isNested && 'pl-6')}>
                {/* Left: room identity */}
                <div className="flex-1 min-w-0">
                  <div className="flex h-5 items-center gap-2 overflow-hidden whitespace-nowrap">
                    {isNested && (
                      <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-primary/70" fill="currentColor" aria-hidden="true">
                        <path d={LINK_GLYPH} />
                      </svg>
                    )}
                    <span
                      title={rt.name}
                      className={cn('truncate text-sm text-foreground', isParent ? 'font-bold' : 'font-semibold')}
                    >
                      {rt.name}
                    </span>
                    {isParent && (
                      <span className="inline-flex h-5 shrink-0 items-center rounded-full bg-primary/10 px-1.5 text-[10px] font-semibold tabular-nums text-primary">
                        {rt.childCount} linked
                      </span>
                    )}
                    {isChild &&
                      (detached ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge className="h-5 shrink-0 cursor-default border border-amber-400/50 bg-amber-100 text-[10px] font-medium tabular-nums text-amber-700 hover:bg-amber-100">
                              Priced separately
                              {storedRule.kind === 'separate' && storedRule.pct !== '' ? ` · engine ${signedPct(storedRule.pct)}` : ''}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="max-w-[260px] text-xs">
                            Priced on its own — no longer following {rt.parentName ?? 'its lead room'}
                            {row.reason ? ` · “${row.reason}”` : ''}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex h-5 shrink-0 cursor-default items-center rounded-full bg-primary/10 px-1.5 text-[10px] font-medium tabular-nums text-primary">
                              {followOffsetShort(rt, sym)}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="text-xs">
                            Follows {rt.parentName ?? 'parent'} at {followOffsetShort(rt, sym)}
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    {isChild && detached && !isAwaiting && (
                      <button
                        type="button"
                        onClick={() => relinkChild(rt.id)}
                        className="inline-flex h-5 shrink-0 items-center gap-1 rounded-md px-1.5 text-[10px] font-medium text-muted-foreground transition-colors hover:text-primary"
                        title="Re-link to lead room"
                      >
                        <RotateCcw className="h-3 w-3" />
                        Re-link to lead
                      </button>
                    )}
                    {/* The one rule chip: how the CURRENT price came to be, from the stored
                        row only. A pending edit is reported on the status line, never here. */}
                    {(storedRule.kind === 'fixed' || storedRule.kind === 'relative') && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge className="h-5 shrink-0 cursor-default border border-amber-400/50 bg-amber-100 text-[10px] font-medium tabular-nums text-amber-700 hover:bg-amber-100">
                            {storedRule.kind === 'fixed'
                              ? `Fixed ${formatRateSym(storedRule.amount, sym)}`
                              : `Engine ${signedPct(storedRule.pct)}`}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-[260px] text-xs">
                          {storedRule.kind === 'fixed'
                            ? 'A manual price, in place of the engine’s figure'
                            : 'The engine’s figure for each day, moved by this percentage'}
                          {row.reason ? ` · “${row.reason}”` : ''}
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {rowAwaiting && (
                      <span className="ml-auto inline-flex shrink-0 items-center gap-1 text-[10px] font-medium text-primary">
                        <span
                          className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-primary motion-reduce:animate-none"
                          aria-hidden="true"
                        />
                        Recomputing…
                      </span>
                    )}
                  </div>
                  {/* The day's price is the Computed / Price pair on the right —
                      repeating it here read as two different numbers. This line
                      carries only what those columns cannot: where the price
                      stands with the PMS, and which plan it belongs to. */}
                  {/* SECOND LINE: where the price stands with the PMS, then which hotel the room
                      belongs to. The badge sits here rather than beside the name because the name
                      line is the one starved of width. */}
                  <div className="flex flex-wrap items-center gap-x-1.5">
                  {rate && (
                    <div className="mt-1 h-4 truncate text-xs text-muted-foreground tabular-nums">
                      <span className="inline-flex items-center gap-1">
                        <span className={cn(
                          'inline-block h-1.5 w-1.5 rounded-full',
                          pmsState === 'synced' ? 'bg-emerald-500' : pmsState === 'pending' ? 'bg-amber-500' : 'bg-slate-300'
                        )} />
                        <span className={cn(
                          pmsState === 'synced' ? 'text-emerald-600' : pmsState === 'pending' ? 'text-amber-600' : 'text-muted-foreground/50'
                        )}>
                          {pmsState === 'never'
                            ? 'Not in the PMS yet'
                            : pmsState === 'synced'
                              ? `PMS ${formatRateSym(rate.pushedRateAmount as number, sym)}`
                              : `PMS ${formatRateSym(rate.pushedRateAmount as number, sym)} · pushing ${formatRateSym(rate.rateAmount, sym)}…`}
                        </span>
                      </span>
                      {mixedPlans && rate.ratePlanName && (
                        <span className="ml-1.5 text-muted-foreground/60">· {rate.ratePlanName}</span>
                      )}
                    </div>
                  )}
                  </div>
                  {/* No "Follows X" line here — the link arrow and the offset
                      badge on the name already say it, and a third statement of
                      the same fact cost a row of height on every child. */}
                  {rowErrors && <p className="mt-1 text-[11px] text-rose-600">{rowErrors.join(', ')}</p>}
                </div>

                {/* Centre: Now · Engine · % · After · undo */}
                <div className="flex w-[384px] shrink-0 flex-col gap-1">
                  <div className="flex items-center gap-2">
                    {/* Now — what the room sells at today. Read-only. */}
                    <div
                      className="flex h-9 w-[72px] items-center justify-center rounded-lg bg-muted/60 text-sm font-semibold tabular-nums text-foreground"
                      aria-label={`Current price for ${rt.name}`}
                    >
                      <AnimatedRate value={currentRate} sym={sym} />
                    </div>
                    {/* Engine — the engine's own figure. For a room that simply follows its
                        lead there is no separate engine figure to show. */}
                    <div
                      className="flex h-9 w-[72px] items-center justify-center rounded-lg bg-muted/40 text-sm font-medium tabular-nums text-muted-foreground"
                      aria-label={`Engine price for ${rt.name}`}
                    >
                      {isChild && !detached ? <span className="text-muted-foreground/50">—</span> : <AnimatedRate value={computedRate} sym={sym} />}
                    </div>

                    {/* % — relative mode. Holds a real value, never a hint: when a
                        change is fanned across the property every room shows it,
                        a linked child included. */}
                    <div className="relative w-[92px]">
                      <Input
                        type="number"
                        step="1"
                        inputMode="decimal"
                        disabled={isAwaiting || row.clearOverride}
                        aria-label={`Percentage adjustment for ${rt.name}`}
                        className={cn(
                          'h-9 w-full pr-6 text-center tabular-nums transition-colors duration-200',
                          inRelativeMode && !draftedPreview?.clamped && !mirrorsLead && 'border-primary bg-primary/5 font-semibold text-primary',
                          inRelativeMode && draftedPreview?.clamped && 'border-amber-400 bg-amber-50 font-semibold',
                          // A mirrored child is a readout of the lead's move, not
                          // its own instruction — shown, but visibly secondary.
                          mirrorsLead && 'border-dashed border-primary/40 bg-primary/[0.03] font-medium text-primary/70'
                        )}
                        placeholder="—"
                        value={row.pct}
                        onChange={(e) => onPctChange(rt.id, e.target.value)}
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground">
                        %
                      </span>
                    </div>

                    {/* Price — fixed mode, or the derived result of a % */}
                    <div className="relative w-[96px]">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        disabled={isAwaiting}
                        aria-label={`Price for ${rt.name}`}
                        className={cn(
                          'h-9 w-full text-center tabular-nums transition-colors duration-200',
                          isOverride && !isChild && !row.clearOverride && 'pr-6',
                          priceIsDerived && 'border-dashed bg-muted/40 font-medium text-muted-foreground',
                          aboveCeiling && 'border-amber-400 bg-amber-50',
                          belowFloor && 'border-rose-400 bg-rose-50',
                          previewLoading && priceIsDerived && 'opacity-70'
                        )}
                        placeholder="—"
                        value={priceValue}
                        onFocus={(e) => {
                          // Typing over a derived value is how a % row switches to
                          // a fixed price, and how a following child detaches.
                          if (priceIsDerived) e.currentTarget.select()
                        }}
                        onChange={(e) => onRateManual(rt.id, e.target.value)}
                      />
                      {/* Remove — the third intent, named. Available on every overridden lead row,
                          a percentage one included (it used to hide there, which is how operators
                          ended up emptying the box instead). */}
                      {isOverride && !isChild && !row.clearOverride && !isAwaiting && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 text-rose-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
                              onClick={() => updateRow(rt.id, { clearOverride: true, rateAmount: '', pct: '' })}
                              aria-label={`Remove override on ${rt.name}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="max-w-[240px] text-xs">
                            Remove the override — back to the engine’s price
                          </TooltipContent>
                        </Tooltip>
                      )}
                      {pctResolving && (
                        <Skeleton className="pointer-events-none absolute inset-y-1 left-1 right-1 rounded-md" />
                      )}
                    </div>

                    {/* One-click way out of whatever this row is doing. On a room
                        a lead is moving it HOLDS the room where it is; anywhere
                        else it just undoes the pending edit. Reaching into the
                        field to delete its contents does the same thing, but
                        nobody should have to discover that. */}
                    <div className="flex w-5 shrink-0 items-center justify-center">
                      {!isAwaiting && (mirrorsLead || dirty) && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={() => (mirrorsLead ? holdAtCurrent(rt.id) : revertRow(rt.id))}
                              aria-label={
                                mirrorsLead
                                  ? `Hold ${rt.name} at its current price`
                                  : `Undo the change to ${rt.name}`
                              }
                              className="rounded p-0.5 text-muted-foreground/50 transition-colors hover:bg-muted hover:text-foreground"
                            >
                              <Undo2 className="h-3.5 w-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="max-w-[240px] text-xs">
                            {mirrorsLead
                              ? `Hold at ${currentRate != null ? formatRateSym(currentRate, sym) : 'its current price'} — don’t move it with ${rt.parentName ?? 'the lead'}`
                              : 'Undo this room’s change'}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </div>

                  {/* Reserved status line: one message at a time, priority-ordered.
                      The derived-plan count rides along here rather than owning a
                      second reserved row of its own on every single room. */}
                  <div className="flex h-4 w-full items-center justify-end gap-1.5 overflow-hidden pr-1">
                    {draftedPreview != null && draftedPreview.derived_plan_count > 0 && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className={cn('shrink-0 cursor-default rounded bg-muted px-1 text-[10px] tabular-nums text-muted-foreground/80 transition-opacity', previewLoading && 'opacity-60')}>
                            +{draftedPreview.derived_plan_count}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-[240px] text-xs">
                          {draftedPreview.derived_plan_count} derived rate{' '}
                          {draftedPreview.derived_plan_count === 1 ? 'plan re-bases' : 'plans re-base'} automatically
                          from this price
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {row.clearOverride ? (
                      isChild ? (
                        <PriceMove
                          from={currentRate}
                          to={draftedPreview?.applied_amount ?? null}
                          rule="resumes following"
                          loading={previewLoading}
                          sym={sym}
                        />
                      ) : (
                        <PriceMove
                          from={currentRate}
                          to={draftedPreview?.applied_amount ?? null}
                          rule="back to engine"
                          loading={previewLoading}
                          sym={sym}
                        />
                      )
                    ) : following ? (
                      // Its number comes from the lead, so report the move, not a
                      // resolution it is not waiting on.
                      followPreview?.clamped ? (
                        <span className="truncate text-[10px] font-medium tabular-nums text-amber-600">
                          capped at a limit
                        </span>
                      ) : followPreview != null && followValue != null && currentRate != null && followValue !== currentRate ? (
                        <PriceMove
                          from={currentRate}
                          to={followValue}
                          rule={`follows ${followOffsetShort(rt, sym)}`}
                          loading={previewLoading}
                          sym={sym}
                        />
                      ) : (rt.minRate != null || rt.maxRate != null) ? (
                        <span className="truncate text-[10px] text-muted-foreground/70 tabular-nums">
                          {rt.minRate != null ? formatRateSym(rt.minRate, sym) : '—'} — {rt.maxRate != null ? formatRateSym(rt.maxRate, sym) : '—'}
                        </span>
                      ) : null
                    ) : inRelativeMode ? (
                      pctResolving ? (
                        <span className="truncate text-[10px] text-muted-foreground">Working it out…</span>
                      ) : hasPct ? (
                        <PriceMove
                          from={currentRate}
                          to={pctApplied}
                          rule={
                            draftedPreview?.clamped
                              ? `${signedPct(row.pct)} · capped at ${draftedPreview.clamped_to === 'floor' ? 'floor' : 'ceiling'}`
                              : isRange
                                ? `${signedPct(row.pct)} of each day’s engine price`
                                : `${signedPct(row.pct)} of engine ${engineAmount != null ? formatRateSym(engineAmount, sym) : '—'}`
                          }
                          tone={draftedPreview?.clamped ? (draftedPreview.clamped_to === 'floor' ? 'rose' : 'amber') : undefined}
                          loading={previewLoading}
                          sym={sym}
                        />
                      ) : (rt.minRate != null || rt.maxRate != null) ? (
                        <span className="truncate text-[10px] text-muted-foreground/70 tabular-nums">
                          {rt.minRate != null ? formatRateSym(rt.minRate, sym) : '—'} — {rt.maxRate != null ? formatRateSym(rt.maxRate, sym) : '—'}
                        </span>
                      ) : null
                    ) : appliedDiffers ? (
                      <PriceMove
                        from={currentRate}
                        to={manualPreview.applied_amount}
                        rule={manualPreview.clamped_to ? `capped at ${manualPreview.clamped_to}` : 'saves as'}
                        tone={manualPreview.clamped_to === 'floor' ? 'rose' : manualPreview.clamped_to === 'ceiling' ? 'amber' : undefined}
                        loading={previewLoading}
                        sym={sym}
                      />
                    ) : dirtyRate && isRange ? (
                      <span className="truncate text-[10px] font-medium tabular-nums text-amber-600">
                        same every day
                      </span>
                    ) : dirtyRate && typedVal != null && !aboveCeiling && !belowFloor ? (
                      <PriceMove from={currentRate} to={typedVal} rule="fixed" loading={previewLoading} sym={sym} />
                    ) : aboveCeiling ? (
                      <span className="truncate text-[10px] tabular-nums text-amber-600">
                        Above ceiling ({formatRateSym(rt.maxRate!, sym)})
                      </span>
                    ) : belowFloor ? (
                      <span className="truncate text-[10px] tabular-nums text-rose-600">
                        Below floor ({formatRateSym(rt.minRate!, sym)})
                      </span>
                    ) : (rt.minRate != null || rt.maxRate != null) ? (
                      <span className="truncate text-[10px] text-muted-foreground/70 tabular-nums">
                        {rt.minRate != null ? formatRateSym(rt.minRate, sym) : '—'} — {rt.maxRate != null ? formatRateSym(rt.maxRate, sym) : '—'}
                      </span>
                    ) : null}
                  </div>

                </div>

                {/* Right: restrictions, behind a drill-down */}
                <div className="shrink-0 pt-0.5">
                  <RestrictionsDrilldown
                    row={row}
                    rtId={rt.id}
                    roomName={rt.name}
                    disabled={isAwaiting}
                    updateRow={updateRow}
                  />
                </div>
              </div>
            </div>
          )
        })}

        {roomTypes.length === 0 && (
          <div className="px-6 py-10 text-center text-sm text-muted-foreground">
            No room types configured
          </div>
        )}
      </div>

      {/* Actions footer — pinned */}
      <div className="shrink-0 space-y-2.5 border-t border-border/40 bg-white px-6 py-4">
        {errors[0] && <p className="text-sm text-rose-600">{errors[0].join(', ')}</p>}

        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          {summary.count === 0 && clearTargets.length === 0 ? (
            <span>No price changes yet — pick a %, or type a price.</span>
          ) : (
            <>
              {summary.count > 0 && (
                <span>
                  <span className="font-semibold text-foreground tabular-nums">{summary.count}</span>{' '}
                  {summary.count === 1 ? 'room' : 'rooms'}
                </span>
              )}
              {clearTargets.length > 0 && (
                <span>
                  <span className="font-semibold text-foreground tabular-nums">{clearTargets.length}</span> cleared
                </span>
              )}
              {isRange && (
                <span>
                  · across{' '}
                  <span className="font-semibold text-foreground tabular-nums">
                    {rangePreview?.days ?? '…'}
                  </span>{' '}
                  days
                </span>
              )}
              {summary.follows > 0 && (
                <span>
                  · <span className="font-semibold text-foreground tabular-nums">{summary.follows}</span>{' '}
                  {summary.follows === 1 ? 'room follows' : 'rooms follow'}
                </span>
              )}
              {sameRoomsFollowing > 0 && (
                <span>
                  · <span className="font-semibold text-foreground tabular-nums">{sameRoomsFollowing}</span>{' '}
                  {sameRoomsFollowing === 1 ? 'Same room follows' : 'Same rooms follow'}
                </span>
              )}
              {summary.capped > 0 && !isRange && (
                <span>
                  · <span className="font-semibold text-foreground tabular-nums">{summary.capped}</span> capped
                </span>
              )}
            </>
          )}
        </div>

        <div className="flex items-center gap-3 pt-1">
          <Input
            className="h-9 flex-1 text-xs"
            placeholder="Reason (optional), applies to all rooms saved together"
            aria-label="Reason for these changes"
            disabled={saving || isAwaiting}
            value={batchReason}
            onChange={(e) => setBatchReason(e.target.value)}
          />
          {/* Wide enough for the server's notice, which names the hotel; it wraps to two lines rather
              than truncate the one word that matters. */}
          <span
            aria-live="polite"
            className={cn(
              'inline-flex w-64 shrink-0 items-center justify-end gap-1.5 overflow-hidden text-right text-xs font-medium leading-tight',
              isAwaiting ? 'text-primary' : 'text-emerald-600'
            )}
          >
            {isAwaiting ? (
              <>
                {/* A pulse, not a spinner (change safety X7); still under reduced motion. */}
                <span
                  className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-primary motion-reduce:animate-none"
                  aria-hidden="true"
                />
                {savedNotice ?? 'Saved · recomputing…'}
              </>
            ) : justUpdated ? (
              <>
                <Check className="h-3.5 w-3.5 shrink-0" />
                Prices updated · pushing to PMS
              </>
            ) : null}
          </span>
          <Button type="button" variant="outline" className="rounded-lg" onClick={handleDiscard} disabled={!hasChanges || saving || isAwaiting}>
            <Undo2 className="mr-1.5 h-3.5 w-3.5" />
            Discard
          </Button>
          <Button type="button" className="rounded-lg" onClick={handleSave} disabled={!canSave}>
            {isRange ? (
              clearTargets.length > 0 && summary.count === 0 ? (
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              ) : (
                <Save className="mr-1.5 h-3.5 w-3.5" />
              )
            ) : (
              <Save className="mr-1.5 h-3.5 w-3.5" />
            )}
            Review changes
          </Button>
        </div>
      </div>

      <BulkOverrideReview
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        range={rangePreview}
        fallbackSpan={fallbackSpan}
        loading={previewLoading}
        sym={sym}
        today={today}
        saving={saving}
        clearing={reviewClearing}
        clearRestrictions={clearRestrictions}
        onClearRestrictionsChange={setClearRestrictions}
        releaseOn={releaseOn}
        onReleaseOnChange={setReleaseOn}
        onConfirm={commit}
        identity={pageIdentity}
      />

      <ClearOverridesDialog
        open={clearOpen}
        onOpenChange={(next) => {
          setClearOpen(next)
          if (!next) setClearRestrictions(false)
        }}
        scope={scope}
        saving={saving}
        clearRestrictions={clearRestrictions}
        onClearRestrictionsChange={setClearRestrictions}
        onConfirm={commitClearAll}
      />
    </div>
    </TooltipProvider>
  )
}
